import { Component, Input, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Chart, ChartConfiguration, TooltipItem, registerables } from 'chart.js';
import { Fluid } from '../../../services/rheological.service';

Chart.register(...registerables);

@Component({
  selector: 'app-reynolds-number',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './reynolds-number.component.html',
  styleUrls: ['./reynolds-number.component.css']
})
export class ReynoldsNumberComponent implements OnChanges, AfterViewInit {
  @Input() fluids: Fluid[] = [];
  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;

  private chart: Chart | null = null;
  private initialized = false;

  ngAfterViewInit(): void {
    this.initialized = true;
    if (this.fluids?.length) this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && this.fluids?.length) this.renderChart();
  }

  private renderChart(): void {
    if (!this.chartCanvas) return;
    if (this.chart) { this.chart.destroy(); this.chart = null; }

    const flowRate = Array.from({ length: 150 }, (_, i) => +((i + 1) * 0.1).toFixed(1));
    const colors = ['#0b2d71', '#009dd9', '#769231', '#faab18', '#97002e'];

    // Only render datasets for fluids that were actually added
    const datasets: any[] = this.fluids.map((fluid, i) => ({
      label: fluid.title,
      data: (fluid.rheology?.re ?? []).map((v, j) => ({ x: flowRate[j], y: v })),
      borderColor: colors[i % colors.length],
      backgroundColor: 'transparent',
      borderWidth: 2,
      pointRadius: 3,
      pointStyle: 'circle',
      pointBorderColor: colors[i % colors.length],
      pointBackgroundColor: 'transparent',
      pointHoverRadius: 3,
      fill: false,
      tension: 0.3
    }));

    const config: ChartConfiguration = {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom', align: 'start', labels: { font: { size: 11 }, usePointStyle: false } },
          title: { display: true, text: 'Reynolds Number', font: { size: 13 } },
          tooltip: {
            callbacks: {
              title: () => '',
              label: (item: TooltipItem<any>) =>
                `${item.dataset.label}: ${(item.parsed.x as number).toFixed(0)} : ${(item.parsed.y as number).toFixed(3)}`
            }
          }
        },
        scales: {
          x: { type: 'linear', title: { display: true, text: 'Flow Rate [bpm]' }, min: 0 },
          y: { title: { display: true, text: 'dp/dL [psi/1000ft]' }, min: 0 }
        }
      }
    };

    this.chart = new Chart(this.chartCanvas.nativeElement, config);
  }
}
